// src/features/modelSlice.ts
import { createSlice } from '@reduxjs/toolkit';
import { modelsApi } from '~/api/models-api'; // The RTK query API service
import type { ModelResponse } from '~/api/models-api';

// Define the Model type

// Define the initial state
interface ModelState {
  model: ModelResponse | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ModelState = {
  model: null,
  status: 'idle',
  error: null,
};

// Create the slice
const modelSlice = createSlice({
  name: 'model',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Use RTK Query actions in the extraReducers
    builder
      .addMatcher(modelsApi.endpoints.getModelById.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(modelsApi.endpoints.getModelById.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        state.model = action.payload;
      })
      .addMatcher(modelsApi.endpoints.getModelById.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch model';
      })
      .addMatcher(modelsApi.endpoints.updateModel.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(modelsApi.endpoints.updateModel.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addMatcher(modelsApi.endpoints.updateModel.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to update model';
      })
      .addMatcher(modelsApi.endpoints.deleteArchivedModel.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(modelsApi.endpoints.deleteArchivedModel.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.model?.response.id === action.meta.arg.originalArgs) {
          state.model = null;
        }
      })
      .addMatcher(modelsApi.endpoints.deleteArchivedModel.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to delete model';
      })

      .addMatcher(modelsApi.endpoints.trainModel.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(modelsApi.endpoints.trainModel.matchFulfilled, (state, action) => {
        state.status = 'succeeded';
        // Optionally handle the training result
      })
      .addMatcher(modelsApi.endpoints.trainModel.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to train model';
      });
  },
});

// Export the reducer
export default modelSlice.reducer;
